import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  bulkAssignVehiclesToPartner,
  bulkUnassignVehiclesFromPartner,
  createPartner,
  deletePartner,
  getPartners,
  updatePartner,
} from "@/v2/apis/partners/partners-apis";
import { queryKeys } from "./keys";

export function usePartners(whitelabelId: number | null) {
  return useQuery({
    queryKey: queryKeys.partners.list(whitelabelId),
    queryFn: () => getPartners(whitelabelId),
    enabled: whitelabelId !== null,
  });
}

export function useCreatePartner(whitelabelId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createPartner(name, whitelabelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.partners.all });
    },
  });
}

export function useUpdatePartner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ partnerId, name }: { partnerId: number; name: string }) =>
      updatePartner(partnerId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.partners.all });
    },
  });
}

export function useDeletePartner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (partnerId: number) => deletePartner(partnerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.partners.all });
    },
  });
}

export function useBulkAssignVehiclesToPartner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      partnerId,
      vehicleCodes,
    }: {
      partnerId: number;
      vehicleCodes: string[];
    }) => bulkAssignVehiclesToPartner(partnerId, vehicleCodes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.partners.all });
    },
  });
}

export function useBulkUnassignVehiclesFromPartner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vehicleCodes: string[]) =>
      bulkUnassignVehiclesFromPartner(vehicleCodes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.partners.all });
    },
  });
}
